import { formatHumanName } from "@medplum/core";
import { Patient } from "@repo/domain";
import type { PortalProfile } from "@repo/edge/types";
import { useTheme } from "@repo/ui/components/theme-provider";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@repo/ui/components/ui/breadcrumb";
import { SidebarInset, SidebarTrigger } from "@repo/ui/components/ui/sidebar";
import { Toaster } from "@repo/ui/components/ui/sonner";
import { cn } from "@repo/ui/lib/utils";
import { Link, Outlet, useMatches, useNavigate, useParams, useRouter, useRouterState } from "@tanstack/react-router";
import { type ComponentType, type ReactNode, useState, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { ConsentSteps, coreConsentForms } from "@/features/consents";
import { signOut, switchPatient } from "@/shared/edge/client";
import { useConsentForms, useConsentStatus, usePatientQuestionnaires } from "@/shared/edge/queries";
import { session } from "@/shared/edge/session";
import { patientDomainIdFromProfile, selectedPatientFromProfile } from "@/shared/lib/patient-profile";
import { AppSidebar, NavGroup } from "./app-sidebar";
import { navItems } from "./nav";
import { PortalSidebarProvider } from "./portal-sidebar";
export function PortalShell({ profile }: { profile: PortalProfile | undefined }) {
  return (
    <PortalSidebarProvider>
      <ShellContent profile={profile} />
    </PortalSidebarProvider>
  );
}
function ShellContent({ profile }: { profile: PortalProfile | undefined }) {
  const router = useRouter();
  const [switching, setSwitching] = useState(false);
  const pendingMutations = useSyncExternalStore(session.subscribe, session.getPendingMutations);
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const canSwitchPatient = Object.keys(useParams({ strict: false })).length === 0;
  const actorHumanName = profile?.actor.reference.startsWith("Patient/")
    ? Patient.displayName(profile.actor.name)
    : profile?.actor.name[0];
  const actorName = actorHumanName ? formatHumanName(actorHumanName) : "Your account";
  const selected = selectedPatientFromProfile(profile);
  const patientName = formatHumanName(Patient.displayName(selected?.name)) || "the patient";
  const fullBleed = useMatches({ select: (matches) => matches.some((match) => match.staticData.fullBleed) });
  const hideHeader = useMatches({ select: (matches) => matches.some((match) => match.staticData.hideHeader) });
  const questionnaires = usePatientQuestionnaires({ patientId: patientDomainIdFromProfile(profile) });
  const consents = useConsentForms({ patientId: patientDomainIdFromProfile(profile) });
  const consent = useConsentStatus({ patientId: patientDomainIdFromProfile(profile) });
  const questionnaireCount = questionnaires.filter((item) => item.status !== "Completed").length;
  let sidebarGroup: ReactNode = null;
  if (!consent.isPending && !consent.isError && !consent.hasConsented && !consents.isPending) {
    sidebarGroup = <ConsentSteps forms={coreConsentForms(consents.forms)} />;
  } else if (!consent.isPending) {
    sidebarGroup = <NavGroup questionnaireCount={questionnaireCount} />;
  }
  const navItem = navItems.find((item) => (item.to === "/" ? pathname === "/" : pathname.startsWith(item.to)));
  const section = navItem?.title ?? "Home";
  const Crumb = useMatches({
    select: (matches) => matches.findLast((match) => match.staticData.crumb)?.staticData.crumb,
  });
  const logout = async () => {
    await signOut();
    await router.invalidate();
    await navigate({ to: "/" });
  };
  const selectPatient = async (patientId: string) => {
    setSwitching(true);
    try {
      await switchPatient(patientId);
      await navigate({ to: "/" });
      await router.invalidate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not switch patient");
    } finally {
      setSwitching(false);
    }
  };
  return (
    <>
      <AppSidebar
        name={actorName}
        viewingFor={selected?.actingOnBehalf ? patientName : undefined}
        theme={resolvedTheme}
        onToggleTheme={() => setTheme(resolvedTheme === "light" ? "dark" : "light")}
        onLogout={() => void logout()}
      >
        {sidebarGroup}
      </AppSidebar>
      <SidebarInset
        className={fullBleed ? "h-svh overflow-hidden bg-background text-foreground" : "bg-background text-foreground"}
      >
        <header
          className={cn(
            "flex h-14 shrink-0 items-center gap-3 px-6 text-base font-medium text-muted-foreground",
            hideHeader ? "md:hidden" : "border-b",
          )}
        >
          <SidebarTrigger className="md:hidden" aria-label="Open menu" />
          {hideHeader ? null : <HeaderTitle section={section} navTo={navItem?.to} Crumb={Crumb} />}
          {profile && (
            <div className="ml-auto flex min-w-0 items-center gap-2 text-sm">
              <span className="text-muted-foreground">Patient</span>
              {profile.patients.length > 1 && canSwitchPatient ? (
                <select
                  aria-label="Patient"
                  className="max-w-48 rounded-md border bg-background px-2 py-1 text-foreground"
                  value={profile.selectedPatientId}
                  disabled={switching || pendingMutations > 0}
                  onChange={(event) => void selectPatient(event.target.value)}
                >
                  {profile.patients.map((patient) => (
                    <option key={patient.patientId} value={patient.patientId}>
                      {formatHumanName(Patient.displayName(patient.name)) || "Patient"}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="truncate text-foreground">
                  {formatHumanName(Patient.displayName(selected?.name)) || "Patient"}
                </span>
              )}
            </div>
          )}
        </header>
        <main className={fullBleed ? "min-h-0 flex-1 overflow-hidden" : "flex flex-1 flex-col"}>
          <Outlet key={profile?.selectedPatientId} />
        </main>
      </SidebarInset>
      <Toaster />
    </>
  );
}

function HeaderTitle({ section, navTo, Crumb }: { section: string; navTo?: string; Crumb?: ComponentType }) {
  if (!Crumb || !navTo) return section;
  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList className="flex-nowrap text-base">
        <BreadcrumbItem>
          <BreadcrumbLink render={<Link to={navTo} />}>{section}</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem className="min-w-0">
          <BreadcrumbPage className="truncate">
            <Crumb />
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
